import React from "react";
import {
  Avatar,
  createTheme,
  Box,
  Card,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Button,
  Typography,
  Chip,
  Divider,
  CircularProgress,
  MenuItem,
  Stack,
  Tooltip,
  Collapse,
  IconButton,
} from "@mui/material";

import Client from "../Client";
import Config from "../ClientConfig";
import BackofficeReportsApi from "./backofficeReportsApi";

import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";

let theme = createTheme();

const objectToCsv = function (data) {
  const csvRows = [];
  if (!data || data.length === 0) return "";

  const headers = Object.keys(data[0]);
  csvRows.push(headers.join(","));

  for (const row of data) {
    const values = headers.map((header) => {
      const val = row[header] ?? "";
      return `"${String(val).replaceAll('"', '""')}"`;
    });
    csvRows.push(values.join(","));
  }
  return csvRows.join("\n");
};

export const feedbackToReportRow = (feedback, index = 0) => ({
  id: feedback.iref ?? index + 1,
  iref: feedback.iref,
  order_id: feedback.order_id,
  order_createdOn: feedback.order_createdOn,
  feedback_createdOn: feedback.createdOn,
  client_email: feedback.client_email || "",
  score: feedback.score,
  details: String(feedback.details || "")
    .replace(/\r\n/g, "\n")
    .replace(/\n\s*\n+/g, "\n")
    .trim(),
  order_location: feedback.order_location,
});

export const feedbackToCsvRow = (feedback) => ({
  iref: feedback.iref,
  order_id: feedback.order_id,
  order_createdOn: feedback.order_createdOn,
  feedback_createdOn: feedback.createdOn,
  client_email: feedback.client_email || "",
  score: feedback.score,
  details: feedback.details,
  order_location: feedback.order_location,
});

class OrderFeedbackView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      client: new Client("none"),

      // table paging
      page: 0,
      rowsPerPage: 25,

      // UI
      loading: false,
      error: "",

      // filters
      period: "last7",
      dateFrom: "",
      dateTo: "",
      score: "",
      locationId: "",
      locations: [],

      // data
      feedbackList: [],
      summary: { metrics: { total: 0, avg_score: null }, by_score: [] },

      // keep last query (like your pages)
      filterData: {},

      // collapse filters
      showFilters: false,
    };

    this.getData = this.getData.bind(this);
    this.handleChangePage = this.handleChangePage.bind(this);
    this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this);
    this.onDownload = this.onDownload.bind(this);
    this.applyPeriod = this.applyPeriod.bind(this);

    this.getLocationNameById = this.getLocationNameById.bind(this);
    this.getPeriodLabel = this.getPeriodLabel.bind(this);
    this.renderAppliedFilters = this.renderAppliedFilters.bind(this);
  }

  componentDidMount() {
    const now = new Date();
    const from = this.startOfDay(this.addDays(now, -6));
    const to = this.endOfDay(now);

    // keep your existing response shape
    const resp = BackofficeReportsApi.getLocations(this.state.client);
    const locations = Array.isArray(resp?.locations) ? resp.locations : [];

    const showFiltersDefault = false;

    this.setState(
      {
        dateFrom: this.toLocalInputValue(from),
        dateTo: this.toLocalInputValue(to),
        locations: locations,
        showFilters: showFiltersDefault,
      },
      this.getData
    );
  }

  // ---------- date helpers ----------
  pad2(n) {
    return String(n).padStart(2, "0");
  }

  toLocalInputValue(d) {
    const yyyy = d.getFullYear();
    const mm = this.pad2(d.getMonth() + 1);
    const dd = this.pad2(d.getDate());
    const hh = this.pad2(d.getHours());
    const mi = this.pad2(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  }

  fromLocalInputValue(s) {
    if (!s) return null;
    const [datePart, timePart] = s.split("T");
    if (!datePart || !timePart) return null;
    const [y, m, d] = datePart.split("-").map((x) => parseInt(x, 10));
    const [hh, mi] = timePart.split(":").map((x) => parseInt(x, 10));
    return new Date(y, (m || 1) - 1, d || 1, hh || 0, mi || 0, 0, 0);
  }

  startOfDay(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }
  endOfDay(d) {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
  }
  addDays(d, days) {
    const x = new Date(d);
    x.setDate(x.getDate() + days);
    return x;
  }
  startOfMonth(d) {
    const x = new Date(d);
    x.setDate(1);
    x.setHours(0, 0, 0, 0);
    return x;
  }
  endOfMonth(d) {
    const x = new Date(d);
    x.setMonth(x.getMonth() + 1, 1);
    x.setHours(0, 0, 0, 0);
    x.setMilliseconds(-1);
    return x;
  }

  applyPeriod(period) {
    const now = new Date();
    if (period === "today") {
      this.setState({
        period,
        dateFrom: this.toLocalInputValue(this.startOfDay(now)),
        dateTo: this.toLocalInputValue(this.endOfDay(now)),
        page: 0,
      });
      return;
    }
    if (period === "yesterday") {
      const y = this.addDays(now, -1);
      this.setState({
        period,
        dateFrom: this.toLocalInputValue(this.startOfDay(y)),
        dateTo: this.toLocalInputValue(this.endOfDay(y)),
        page: 0,
      });
      return;
    }
    if (period === "last7") {
      this.setState({
        period,
        dateFrom: this.toLocalInputValue(this.startOfDay(this.addDays(now, -6))),
        dateTo: this.toLocalInputValue(this.endOfDay(now)),
        page: 0,
      });
      return;
    }
    if (period === "thisMonth") {
      this.setState({
        period,
        dateFrom: this.toLocalInputValue(this.startOfMonth(now)),
        dateTo: this.toLocalInputValue(this.endOfDay(now)),
        page: 0,
      });
      return;
    }
    if (period === "lastMonth") {
      const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      this.setState({
        period,
        dateFrom: this.toLocalInputValue(this.startOfMonth(lm)),
        dateTo: this.toLocalInputValue(this.endOfMonth(lm)),
        page: 0,
      });
      return;
    }
    this.setState({ period, page: 0 });
  }

  buildQueryData() {
    const queryData = {};
    const from = this.fromLocalInputValue(this.state.dateFrom);
    const to = this.fromLocalInputValue(this.state.dateTo);

    if (from) queryData["createdOn__gte"] = from.toISOString();
    if (to) queryData["createdOn__lte"] = to.toISOString();

    if (this.state.score !== "" && this.state.score !== null) queryData["score"] = this.state.score;
    if (this.state.locationId && this.state.locationId !== "") queryData["location"] = this.state.locationId;

    return queryData;
  }

  getData() {
    const queryData = this.buildQueryData();
    this.setState({ loading: true, error: "" }, () => {
      try {
        const list = this.state.client.getData(Config.API_URL_ORDERS_FEEDBACK, queryData);
        const summary = this.state.client.getData(Config.API_URL_ORDERS_FEEDBACK_SUMMARY, queryData);

        this.setState({
          feedbackList: Array.isArray(list) ? list : [],
          summary: summary || { metrics: { total: 0, avg_score: null }, by_score: [] },
          loading: false,
          filterData: queryData,
        });
      } catch (e) {
        this.setState({
          loading: false,
          error: e?.message ? String(e.message) : "Failed to load feedback",
        });
      }
    });
  }

  handleChangePage = (event, newPage) => {
    this.setState({ page: newPage });
  };
  handleChangeRowsPerPage = (event) => {
    this.setState({ rowsPerPage: +event.target.value, page: 0 });
  };

  onDownload() {
    const link = document.createElement("a");
    const rows = (this.state.feedbackList || []).map(feedbackToCsvRow);

    const csvData = rows.length > 0 ? objectToCsv(rows) : "";
    const from = this.state.filterData?.createdOn__gte ?? "";
    const to = this.state.filterData?.createdOn__lte ?? "";
    const score = this.state.filterData?.score ?? "";
    const location = this.state.filterData?.location ?? "";

    const header =
      `Feedback export\n` +
      `From: ${from}\n` +
      `To: ${to}\n` +
      `Score filter: ${score}\n` +
      `Location filter: ${location}\n` +
      `Total: ${this.state.summary?.metrics?.total ?? 0}\n` +
      `Avg score: ${this.state.summary?.metrics?.avg_score ?? ""}\n`;

    const csv = "\uFEFF" + header + "\n" + csvData;

    link.download = `order_feedback_${new Date().toISOString().slice(0, 10)}.csv`;
    link.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    link.click();
  }

  // ---------------- UI helpers ----------------
  getPeriodLabel(period) {
    switch (period) {
      case "today":
        return "Today";
      case "yesterday":
        return "Yesterday";
      case "last7":
        return "Last 7 days";
      case "thisMonth":
        return "This month";
      case "lastMonth":
        return "Last month";
      case "custom":
        return "Custom";
      default:
        return String(period || "");
    }
  }

  getLocationNameById(id) {
    if (!id) return "";
    const loc = (this.state.locations || []).find((x) => String(x.iref) === String(id));
    return loc?.name || String(id);
  }

  renderAppliedFilters() {
    const fromIso = this.state.filterData?.createdOn__gte ?? "";
    const toIso = this.state.filterData?.createdOn__lte ?? "";
    const score = this.state.filterData?.score ?? "";
    const locationId = this.state.filterData?.location ?? "";

    return (
      <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 0.75 }}>
        <Chip size="small" variant="outlined" label={`Period: ${this.getPeriodLabel(this.state.period)}`} sx={{ borderRadius: "10px" }} />
        {fromIso ? <Chip size="small" variant="outlined" label={`From: ${new Date(fromIso).toLocaleString("ro-RO")}`} sx={{ borderRadius: "10px" }} /> : null}
        {toIso ? <Chip size="small" variant="outlined" label={`To: ${new Date(toIso).toLocaleString("ro-RO")}`} sx={{ borderRadius: "10px" }} /> : null}
        {locationId ? <Chip size="small" variant="outlined" label={`Location: ${this.getLocationNameById(locationId)}`} sx={{ borderRadius: "10px" }} /> : null}
        {score ? (
          <Chip
            size="small"
            variant="outlined"
            label={`Score: ${score}`}
            sx={{ borderRadius: "10px" }}
            onDelete={() => this.setState({ score: "" }, this.getData)}
          />
        ) : null}
      </Stack>
    );
  }

  renderHeader() {
    return (
      <Box sx={{ px: { xs: 1, sm: 2 }, pt: { xs: 1, sm: 2 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.25}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.1 }}>
              Order Feedback
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            {this.state.loading ? <CircularProgress size={18} /> : null}

            <Tooltip title={this.state.showFilters ? "Hide filters" : "Show filters"}>
              <IconButton
                size="small"
                onClick={() => this.setState({ showFilters: !this.state.showFilters })}
                sx={{ borderRadius: "10px", border: "1px solid rgba(0,0,0,0.15)" }}
              >
                <FilterAltIcon fontSize="small" />
                {this.state.showFilters ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Tooltip>

            <Tooltip title="Search">
              <span>
                <Button
                  color="inherit"
                  size="small"
                  onClick={this.getData}
                  variant="contained"
                  style={{ maxWidth: "36px", minWidth: "36px", maxHeight: "36px", minHeight: "36px" }}
                  disabled={this.state.loading}
                >
                  <Avatar sx={{ width: 24, height: 24 }}>
                    <SearchIcon />
                  </Avatar>
                </Button>
              </span>
            </Tooltip>

            <Tooltip title="Download CSV">
              <span>
                <Button
                  color="inherit"
                  size="small"
                  onClick={this.onDownload}
                  variant="contained"
                  style={{ maxWidth: "36px", minWidth: "36px", maxHeight: "36px", minHeight: "36px" }}
                  disabled={this.state.loading || (this.state.feedbackList || []).length === 0}
                >
                  <Avatar sx={{ width: 24, height: 24, bgcolor: theme.palette.success.light }}>
                    <FileDownloadIcon />
                  </Avatar>
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {this.state.error ? (
          <Typography color="error" sx={{ mt: 1 }}>
            {this.state.error}
          </Typography>
        ) : null}

        <Box sx={{ mt: 0.75 }}>{this.renderAppliedFilters()}</Box>
      </Box>
    );
  }

  renderFiltersPanel() {
    return (
      <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1 }}>
        <Collapse in={this.state.showFilters} timeout="auto" unmountOnExit>
          <Card variant="outlined" sx={{ borderRadius: "14px", p: { xs: 1, sm: 1.5 } }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={{ xs: 1, md: 2 }} alignItems="stretch" flexWrap="wrap">
              <TextField
                select
                label="Period"
                size="small"
                value={this.state.period}
                onChange={(e) => this.applyPeriod(e.target.value)}
                sx={{ minWidth: { xs: "100%", sm: 160 } }}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="yesterday">Yesterday</MenuItem>
                <MenuItem value="last7">Last 7 days</MenuItem>
                <MenuItem value="thisMonth">This month</MenuItem>
                <MenuItem value="lastMonth">Last month</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </TextField>

              <TextField
                label="From"
                size="small"
                type="datetime-local"
                value={this.state.dateFrom}
                onChange={(e) => this.setState({ period: "custom", dateFrom: e.target.value, page: 0 })}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: { xs: "100%", sm: 240 } }}
              />

              <TextField
                label="To"
                size="small"
                type="datetime-local"
                value={this.state.dateTo}
                onChange={(e) => this.setState({ period: "custom", dateTo: e.target.value, page: 0 })}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: { xs: "100%", sm: 240 } }}
              />

              <TextField
                select
                label="Location"
                size="small"
                value={this.state.locationId}
                onChange={(e) => this.setState({ locationId: e.target.value, page: 0 })}
                sx={{ minWidth: { xs: "100%", sm: 220 } }}
              >
                <MenuItem value="">All</MenuItem>
                {(this.state.locations || []).map((l) => (
                  <MenuItem key={l.iref} value={String(l.iref)}>
                    {l.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Score"
                size="small"
                value={this.state.score}
                onChange={(e) => this.setState({ score: e.target.value, page: 0 })}
                sx={{ width: { xs: "100%", sm: 120 } }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="1">1</MenuItem>
                <MenuItem value="2">2</MenuItem>
                <MenuItem value="3">3</MenuItem>
                <MenuItem value="4">4</MenuItem>
                <MenuItem value="5">5</MenuItem>
              </TextField>

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                {/* Search */}
                <Button
                  size="small"
                  variant="contained"
                  onClick={this.getData}
                  disabled={this.state.loading}
                  sx={{ minWidth: 90 }}
                >
                  Search
                </Button>

                {/* Clear (X icon) */}
                <IconButton
                  size="small"
                  onClick={() =>
                    this.setState(
                      { score: "", locationId: "", page: 0 },
                      this.getData
                    )
                  }
                  disabled={
                    this.state.loading ||
                    (this.state.score === "" && this.state.locationId === "")
                  }
                  sx={{
                    border: "1px solid rgba(0,0,0,0.12)",
                    borderRadius: "10px",
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          </Card>
        </Collapse>
      </Box>
    );
  }

  renderCompactSummary() {
    const total = this.state.summary?.metrics?.total ?? 0;
    const avg = this.state.summary?.metrics?.avg_score;
    const byScore = this.state.summary?.by_score ?? [];

    return (
      <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1 }}>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" alignItems="stretch">
          <Chip variant="outlined" label={`Total: ${total}`} sx={{ borderRadius: "10px" }} />
          <Chip variant="outlined" label={`Avg: ${avg == null ? "-" : Number(avg).toFixed(2)}`} sx={{ borderRadius: "10px" }} />
        </Stack>

        <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1 }}>
          {byScore.length === 0 ? (
            <Chip size="small" label="No data" />
          ) : (
            byScore.map((x) => (
              <Chip
                key={`dist-${x.score}`}
                size="small"
                variant="outlined"
                label={`${x.score}: ${x.count}`}
                onClick={() => this.setState({ score: String(x.score), page: 0 }, this.getData)}
                sx={{ cursor: "pointer", mb: 0.5, borderRadius: "10px" }}
              />
            ))
          )}
        </Stack>
      </Box>
    );
  }

  renderFeedbackTable() {
    const page = this.state.page;
    const rowsPerPage = this.state.rowsPerPage;

    const rows = (this.state.feedbackList || []).map(feedbackToReportRow);

    const paged = rowsPerPage > 0 ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : rows;

    return (
      <Box sx={{ px: { xs: 1, sm: 2 }, py: 1.5 }}>
        <Card variant="outlined" sx={{ borderRadius: "14px", overflow: "hidden" }}>
          {/* Mobile layout */}
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            {paged.map((r) => (
              <Box key={r.id} sx={{ p: 1.25, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      Order #{r.order_id}
                    </Typography>
                  </Box>

                  <Chip size="small" variant="outlined" label={r.score ?? 0} sx={{ borderRadius: "10px" }} />
                </Stack>

                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                  Order created: {r.order_createdOn ? new Date(r.order_createdOn).toLocaleString("ro-RO") : "-"}
                </Typography>

                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  Feedback created: {r.feedback_createdOn ? new Date(r.feedback_createdOn).toLocaleString("ro-RO") : "-"}
                </Typography>

                {r.order_location || r.client_email ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    {r.order_location || ""}
                    {r.order_location && r.client_email ? " · " : ""}
                    {r.client_email || ""}
                  </Typography>
                ) : null}

                <Typography
                  variant="body2"
                  sx={{
                    mt: 1,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  {r.details || "-"}
                </Typography>
              </Box>
            ))}

            {paged.length === 0 && !this.state.loading ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" sx={{ opacity: 0.75 }}>
                  No feedback found for selected filters.
                </Typography>
              </Box>
            ) : null}
          </Box>

          {/* Desktop/tablet layout */}
          <TableContainer sx={{ display: { xs: "none", md: "block" } }}>
            <Table stickyHeader aria-label="feedback table" sx={{ tableLayout: "fixed", minWidth: 1200 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ p: "10px", textAlign: "center", width: 80 }}>
                    <b>Order</b>
                  </TableCell>
                  <TableCell sx={{ p: "10px", textAlign: "left", width: 155 }}>
                    <b>Order created</b>
                  </TableCell>
                  <TableCell sx={{ p: "10px", textAlign: "left", width: 155 }}>
                    <b>Feedback created</b>
                  </TableCell>
                  <TableCell sx={{ p: "10px", textAlign: "center", width: 75 }}>
                    <b>Score</b>
                  </TableCell>
                  <TableCell sx={{ p: "10px", textAlign: "left", width: 150 }}>
                    <b>Location</b>
                  </TableCell>
                  <TableCell sx={{ p: "10px", textAlign: "left", width: 220 }}>
                    <b>Client email</b>
                  </TableCell>
                  <TableCell sx={{ p: "10px", textAlign: "left" }}>
                    <b>Details</b>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paged.map((r) => (
                  <TableRow hover key={r.id} tabIndex={-1}>
                    <TableCell sx={{ p: "10px", textAlign: "center" }}>{r.order_id}</TableCell>

                    <TableCell sx={{ p: "10px", textAlign: "left" }}>
                      {r.order_createdOn ? new Date(r.order_createdOn).toLocaleString("ro-RO") : "-"}
                    </TableCell>

                    <TableCell sx={{ p: "10px", textAlign: "left" }}>
                      {r.feedback_createdOn ? new Date(r.feedback_createdOn).toLocaleString("ro-RO") : "-"}
                    </TableCell>

                    <TableCell sx={{ p: "10px", textAlign: "center" }}>
                      <Chip size="small" variant="outlined" label={r.score ?? 0} sx={{ borderRadius: "10px" }} />
                    </TableCell>

                    <TableCell sx={{ p: "10px", textAlign: "left" }}>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {r.order_location || "-"}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ p: "10px", textAlign: "left", overflowWrap: "anywhere" }}>
                      {r.client_email || "-"}
                    </TableCell>

                    <TableCell sx={{ p: "10px", textAlign: "left" }}>
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {r.details || "-"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}

                {paged.length === 0 && !this.state.loading ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ p: "16px" }}>
                      <Typography variant="body2" sx={{ opacity: 0.75 }}>
                        No feedback found for selected filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider />

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, { value: -1, label: "All" }]}
            labelRowsPerPage="Rows:"
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={this.handleChangePage}
            onRowsPerPageChange={this.handleChangeRowsPerPage}
          />
        </Card>
      </Box>
    );
  }

  render() {
    return (
      <Paper
        sx={{
          width: {
            xs: 'calc(100vw - 8px)',
            sm: '100%',
          },
          maxWidth: {
            xs: 'calc(100vw - 8px)',
            sm: '100%',
          },
          ml: {
            xs: 'calc(50% - 50vw + 4px)',
            sm: 0,
          }, 
          borderRadius: { xs: "0px", sm: "16px" },
          overflow: "hidden",
          height: { xs: "100dvh", sm: "100%" },
        }}
      >
        <Card
          sx={{
            borderRadius: { xs: "0px", sm: "16px" },
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header area stays at top */}
          <Box sx={{ flex: "0 0 auto" }}>
            {this.renderHeader()}
            {this.renderFiltersPanel()}
            <Divider sx={{ my: 1.25 }} />
            {this.renderCompactSummary()}
            <Divider sx={{ my: 1.25 }} />
          </Box>

          {/* Scroll area (table + pagination) */}
          <Box sx={{ flex: "1 1 auto", overflow: "auto" }}>
            {this.renderFeedbackTable()}
          </Box>
        </Card>
      </Paper>
    );
  }
}

export default OrderFeedbackView;
